/**
 * 환경 변수 기반 로깅 시스템
 * 배포 환경에서는 콘솔 로그를 숨기고, 개발 환경에서는 모든 로그를 표시합니다.
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4,
}

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  showTimestamp: boolean;
  showLevel: boolean;
}

class Logger {
  private config: LoggerConfig;

  constructor() {
    this.config = this.getConfig();
  }

  private getConfig(): LoggerConfig {
    // 환경 변수에서 로깅 설정 읽기
    const isDevelopment = process.env.NODE_ENV === 'development';
    const debugMode = process.env.REACT_APP_DEBUG === 'true';
    const explicitConsole = process.env.REACT_APP_ENABLE_CONSOLE_LOGS;
    const logLevel = process.env.REACT_APP_LOG_LEVEL;

    // REACT_APP_ENABLE_CONSOLE_LOGS=false 이면 항상 off.
    // true 이면 항상 on(레벨은 LOG_LEVEL).
    // 미설정: 개발 on, 프로덕션 off — 단 REACT_APP_DEBUG=true 도 프로덕션에서 on.
    let enabled: boolean;
    if (explicitConsole === 'false') {
      enabled = false;
    } else if (explicitConsole === 'true' || debugMode) {
      enabled = true;
    } else {
      enabled = isDevelopment;
    }
    
    // 로그 레벨 설정 (기본값: 개발환경=DEBUG, 프로덕션=ERROR)
    let level = LogLevel.DEBUG;
    if (logLevel) {
      switch (logLevel.toLowerCase()) {
        case 'error': level = LogLevel.ERROR; break;
        case 'warn': level = LogLevel.WARN; break;
        case 'info': level = LogLevel.INFO; break;
        case 'debug': level = LogLevel.DEBUG; break;
        case 'trace': level = LogLevel.TRACE; break;
      }
    } else if (!isDevelopment) {
      level = LogLevel.ERROR; // 프로덕션에서는 ERROR만 표시
    }

    return {
      enabled,
      level,
      showTimestamp: process.env.REACT_APP_LOG_TIMESTAMP !== 'false',
      showLevel: process.env.REACT_APP_LOG_SHOW_LEVEL !== 'false',
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && level <= this.config.level;
  }

  private formatMessage(level: string, message: string, ...args: any[]): [string, ...any[]] {
    const parts: string[] = [];
    
    if (this.config.showTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }
    
    if (this.config.showLevel) {
      parts.push(`[${level}]`);
    }
    
    parts.push(message);
    
    return [parts.join(' '), ...args];
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(...this.formatMessage('ERROR', message, ...args));
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.formatMessage('WARN', message, ...args));
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(...this.formatMessage('INFO', message, ...args));
    }
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(...this.formatMessage('DEBUG', message, ...args));
    }
  }

  trace(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.TRACE)) {
      console.log(...this.formatMessage('TRACE', message, ...args));
    }
  }

  // 기존 console.log와 호환성을 위한 메서드
  log(message: string, ...args: any[]): void {
    this.debug(message, ...args);
  }

  // 그룹 로깅 지원
  group(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.groupEnd();
    }
  }

  // 테이블 로깅 지원
  table(data: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.table(data);
    }
  }

  // 현재 설정 정보 반환
  getCurrentConfig(): LoggerConfig {
    return { ...this.config };
  }

  // 런타임에 설정 변경 (디버깅용)
  setConfig(newConfig: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// 싱글톤 인스턴스 생성
export const logger = new Logger();

// 편의를 위한 직접 export
export const log = logger.log.bind(logger);
export const error = logger.error.bind(logger);
export const warn = logger.warn.bind(logger);
export const info = logger.info.bind(logger);
export const debug = logger.debug.bind(logger);
export const trace = logger.trace.bind(logger);

// 환경 변수 설명을 위한 주석
/**
 * 환경 변수 설정 가이드:
 *
 * - 배포 빌드에서 브라우저 콘솔 끄기(기본): REACT_APP_ENABLE_CONSOLE_LOGS 미설정 또는 false
 * - 배포에서 콘솔 켜기: REACT_APP_ENABLE_CONSOLE_LOGS=true (필요 시 REACT_APP_LOG_LEVEL=debug 등)
 * - REACT_APP_DEBUG=true 는 프로덕션에서도 로깅·콘솔 억제 해제(비상 디버깅용)
 *
 * 기타: REACT_APP_LOG_TIMESTAMP, REACT_APP_LOG_SHOW_LEVEL, REACT_APP_LOG_LEVEL
 */
